import { useState, useEffect, useCallback } from "react";
import type { Product } from "@/lib/types";
import { getProducts, saveProduct } from "@/lib/api";

const badgeColors: Record<string, string> = {
  FAVORITO: "bg-[#d4607a]/10 text-[#d4607a]",
  NUEVO: "bg-[#00b4c8]/10 text-[#00b4c8]",
  TEMPORADA: "bg-[#fde68a]/60 text-[#7a5c00]",
  PERSONALIZABLE: "bg-[#c9b8f0]/40 text-[#7c5cbf]",
  CLÁSICO: "bg-[#f9b8c8]/40 text-[#d4607a]",
};

const MAX_FEATURED = 4;

const isActive = (value: unknown) =>
  value === true || value === 1 || value === "1";

const isFeatured = (value: unknown) =>
  value === true || value === 1 || value === "1";

export default function AdminFavoritos() {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterCat, setFilterCat] = useState("Todos");
  const [toast, setToast] = useState("");

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(""), 2500);
  };

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);

      const data = await getProducts();

      const normalized = data.map((p: Product) => ({
        ...p,
        active: isActive(p.active),
        featured_month: isFeatured(p.featured_month),
        price: Number(p.price),
        priority: Number(p.priority),
      }));

      normalized.sort((a: Product, b: Product) => {
        if (a.category === b.category) {
          return a.priority - b.priority;
        }
        return a.category.localeCompare(b.category);
      });

      setProducts(normalized);
    } catch (error) {
      console.error(error);
      showToast("Error al cargar productos.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const categories = [
    "Todos",
    ...Array.from(new Set(products.map((p) => p.category))),
  ];

  const featuredCount = products.filter((p) =>
    isFeatured(p.featured_month)
  ).length;

  const toggleFeatured = async (product: Product) => {
    const currentlyFeatured = isFeatured(product.featured_month);

    if (!currentlyFeatured && featuredCount >= MAX_FEATURED) {
      showToast(`Máximo ${MAX_FEATURED} favoritos del mes. Quita uno primero.`);
      return;
    }

    const newValue = !currentlyFeatured;

    try {
      await saveProduct({
        ...product,
        active: isActive(product.active) ? 1 : 0,
        featured_month: newValue ? 1 : 0,
      });

      setProducts((prev) =>
        prev.map((p) =>
          p.id === product.id
            ? { ...p, featured_month: newValue }
            : p
        )
      );

      showToast(
        newValue
          ? `"${product.name}" agregado a Favoritos del Mes`
          : `"${product.name}" quitado de Favoritos del Mes`
      );
    } catch (error) {
      console.error(error);
      showToast("Error al actualizar. Intenta de nuevo.");
    }
  };

  const filtered = products
    .filter((p) => isActive(p.active))
    .filter((p) => filterCat === "Todos" || p.category === filterCat)
    .filter(
      (p) =>
        search === "" ||
        p.name.toLowerCase().includes(search.toLowerCase()) ||
        p.category.toLowerCase().includes(search.toLowerCase())
    );

  const featured = products.filter((p) => isFeatured(p.featured_month));

  return (
    <div className="space-y-6">
      {toast && (
        <div className="fixed top-4 right-4 z-50 bg-[#2d1a0e] text-white text-sm px-5 py-3 rounded-xl">
          <span className="flex items-center gap-2">
            <i className="ri-information-line text-[#fde68a]"></i>
            {toast}
          </span>
        </div>
      )}

      <div>
        <h1 className="text-xl font-bold text-[#2d1a0e]">
          Favoritos del Mes
        </h1>
        <p className="text-sm text-[#8a6a55] mt-0.5">
          Elige hasta {MAX_FEATURED} productos que aparecerán destacados en el inicio del sitio.
        </p>
      </div>

      <div className="bg-white rounded-xl border border-[#c9b8f0]/30 overflow-hidden">
        <div className="px-5 py-4 border-b border-[#c9b8f0]/20 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="w-5 h-5 flex items-center justify-center">
              <i className="ri-star-fill text-[#fde68a] text-base"></i>
            </span>
            <h2 className="font-semibold text-[#2d1a0e] text-sm">
              Seleccionados actualmente
            </h2>
          </div>

          <span
            className={`text-xs font-bold px-3 py-1 rounded-full ${
              featuredCount >= MAX_FEATURED
                ? "bg-[#d4607a]/10 text-[#d4607a]"
                : "bg-[#00b4c8]/10 text-[#00b4c8]"
            }`}
          >
            {featuredCount} / {MAX_FEATURED}
          </span>
        </div>

        {loading ? (
          <div className="py-10 text-center text-[#8a6a55] text-sm">
            <i className="ri-loader-4-line text-2xl animate-spin text-[#00b4c8] block mx-auto mb-2"></i>
            Cargando...
          </div>
        ) : featured.length === 0 ? (
          <div className="px-5 py-8 text-center text-[#8a6a55] text-sm">
            <span className="w-10 h-10 flex items-center justify-center rounded-full bg-[#f8f4ff] mx-auto mb-3">
              <i className="ri-star-line text-[#c9b8f0] text-xl"></i>
            </span>
            Ningún producto seleccionado aún. Elige hasta {MAX_FEATURED} de la lista de abajo.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 p-5">
            {featured.map((product) => (
              <div
                key={product.id}
                className="relative bg-[#fffbfe] rounded-xl border-2 border-[#fde68a] overflow-hidden"
              >
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                  <span className="absolute top-2 left-2 flex items-center gap-1 bg-[#fde68a] text-[#2d1a0e] text-xs font-bold px-2 py-0.5 rounded-full">
                    <i className="ri-star-fill text-xs"></i> Favorito
                  </span>
                </div>

                <div className="p-3">
                  <p className="text-xs text-[#d4607a] font-medium mb-0.5">
                    {product.category}
                  </p>

                  <p className="font-semibold text-[#2d1a0e] text-sm leading-tight mb-2 line-clamp-1">
                    {product.name}
                  </p>

                  <div className="flex items-center justify-between">
                    <span className="text-base font-bold text-[#2d1a0e]">
                      ${product.price}
                    </span>

                    <button
                      onClick={() => toggleFeatured(product)}
                      className="text-xs text-[#d4607a] hover:text-[#b84d65] font-medium flex items-center gap-1 cursor-pointer whitespace-nowrap transition-colors"
                    >
                      <i className="ri-close-circle-line"></i> Quitar
                    </button>
                  </div>
                </div>
              </div>
            ))}

            {Array.from({ length: MAX_FEATURED - featured.length }).map((_, i) => (
              <div
                key={`empty-${i}`}
                className="aspect-[4/3] rounded-xl border-2 border-dashed border-[#c9b8f0]/40 flex flex-col items-center justify-center text-[#c9b8f0] text-xs gap-1"
              >
                <span className="w-8 h-8 flex items-center justify-center">
                  <i className="ri-add-circle-line text-2xl"></i>
                </span>
                Slot disponible
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-[#8a6a55]">
            <i className="ri-search-line text-sm"></i>
          </span>

          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar producto..."
            className="w-full pl-9 pr-4 py-2.5 rounded-lg border border-[#c9b8f0]/40 bg-white text-sm text-[#2d1a0e] focus:outline-none focus:border-[#00b4c8] transition-colors"
          />
        </div>

        <select
          value={filterCat}
          onChange={(e) => setFilterCat(e.target.value)}
          className="px-3 py-2.5 rounded-lg border border-[#c9b8f0]/40 bg-white text-sm text-[#2d1a0e] focus:outline-none focus:border-[#00b4c8] cursor-pointer"
        >
          {categories.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </div>

      <div className="bg-white rounded-xl border border-[#c9b8f0]/20 overflow-hidden">
        <div className="px-5 py-4 border-b border-[#c9b8f0]/20">
          <h2 className="font-semibold text-[#2d1a0e] text-sm">
            Todos los productos activos
            <span className="ml-2 text-xs text-[#8a6a55] font-normal">
              ({filtered.length} productos)
            </span>
          </h2>
        </div>

        <div className="divide-y divide-[#f0e6d8]">
          {filtered.map((product) => (
            <div
              key={product.id}
              className={`flex items-center gap-4 px-5 py-3.5 transition-colors ${
                isFeatured(product.featured_month)
                  ? "bg-[#fffdf0]"
                  : "hover:bg-[#fdf6f0]"
              }`}
            >
              <img
                src={product.image}
                alt={product.name}
                className="w-12 h-12 rounded-lg object-cover object-top flex-shrink-0"
              />

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-semibold text-[#2d1a0e] truncate">
                    {product.name}
                  </p>

                  {product.badge && (
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        badgeColors[product.badge] || "bg-gray-100 text-gray-500"
                      }`}
                    >
                      {product.badge}
                    </span>
                  )}

                  {isFeatured(product.featured_month) && (
                    <span className="flex items-center gap-0.5 text-xs bg-[#fde68a]/60 text-[#7a5c00] px-2 py-0.5 rounded-full font-semibold">
                      <i className="ri-star-fill text-xs"></i> Favorito del Mes
                    </span>
                  )}
                </div>

                <p className="text-xs text-[#6b5b6e] mt-0.5">
                  {product.category} · ${product.price} MXN
                </p>
              </div>

              <button
                onClick={() => toggleFeatured(product)}
                className={`flex items-center gap-1.5 text-xs font-semibold px-4 py-2 rounded-full transition-all cursor-pointer whitespace-nowrap ${
                  isFeatured(product.featured_month)
                    ? "bg-[#fde68a] text-[#2d1a0e] hover:bg-[#fcd34d]"
                    : featuredCount >= MAX_FEATURED
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-[#f8f4ff] text-[#7c5cbf] hover:bg-[#c9b8f0]/40"
                }`}
              >
                {isFeatured(product.featured_month) ? (
                  <>
                    <i className="ri-star-fill"></i> Quitar
                  </>
                ) : (
                  <>
                    <i className="ri-star-line"></i> Destacar
                  </>
                )}
              </button>
            </div>
          ))}

          {filtered.length === 0 && !loading && (
            <div className="px-5 py-10 text-center text-[#8a6a55] text-sm">
              No se encontraron productos activos
            </div>
          )}

          {loading && (
            <div className="px-5 py-10 text-center text-[#8a6a55] text-sm">
              Cargando productos...
            </div>
          )}
        </div>
      </div>
    </div>
  );
}